import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';

import { Panel, PanelHeader } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useChangePassword } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { NOUN, pluralize } from '@/lib/plural';

const PASSWORD_MIN = 10;

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Введите текущий пароль.'),
    newPassword: z
      .string()
      .min(PASSWORD_MIN, `Минимум ${pluralize(PASSWORD_MIN, NOUN.character)}.`)
      .regex(/[a-z]/, 'Добавьте хотя бы одну строчную букву.')
      .regex(/[A-Z]/, 'Добавьте хотя бы одну заглавную букву.')
      .regex(/[0-9]/, 'Добавьте хотя бы одну цифру.'),
    confirmPassword: z.string().min(1, 'Повторите новый пароль.'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Пароли не совпадают.',
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    path: ['newPassword'],
    message: 'Новый пароль должен отличаться от текущего.',
  });

type FormValues = z.infer<typeof schema>;

export function PasswordSection() {
  const toast = useToast();
  const changePassword = useChangePassword();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await changePassword.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      reset();
      toast.success('Пароль изменён', 'Другие сессии могут быть завершены.');
    } catch (error) {
      setServerError(toMessage(error, 'Не удалось изменить пароль.'));
    }
  });

  return (
    <Panel padding="none">
      <PanelHeader
        title="Пароль"
        description="Смена пароля не влияет на баланс и на платежи, которые уже в работе."
      />

      <form onSubmit={onSubmit} noValidate className="max-w-prose space-y-4 px-4 py-4">
        {serverError ? <InlineError message={serverError} /> : null}

        <TextField
          label="Текущий пароль"
          type="password"
          enterKeyHint="next"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />

        <TextField
          label="Новый пароль"
          type="password"
          autoComplete="new-password"
          hint={`Минимум ${pluralize(PASSWORD_MIN, NOUN.character)}: заглавные и строчные буквы плюс цифра.`}
          error={errors.newPassword?.message}
          {...register('newPassword')}
        />

        <TextField
          label="Повторите новый пароль"
          type="password"
          enterKeyHint="go"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? 'Меняю…' : 'Изменить пароль'}
        </Button>
      </form>
    </Panel>
  );
}
